/*
 *
 * GenericShopList
 *
 * @version 1.0.0
 * @author Marco Alessandroni
 * @since 30-Jun-2025
*/

import React, { useEffect, useState, useContext } from 'react'
import PropTypes from 'prop-types'
import Toggle from 'Hoc/ToggleComponent'
import Dialog from '@mui/material/Dialog'
import FlowTemplate from 'Components/Template/FlowTemplate'
import formattedMessage from './languageModule'
import listPointOfSaleFormattedMessage from 'Components/ListPointOfSales/languageModule'
import { Dismiss } from '@nexidigital/iconlibrary'
import ListPointOfSales from 'Components/ListPointOfSales'
import PortalLoader from '@nexidigital/portalloader'
import { useMobileDetect } from 'Hoc/ListenerProvider'
import { AnalyticsContext } from 'Hoc/AnalyticsProvider'
import { capitalizeFirst } from 'Utility/globalMethods'
import { flowToastMapType } from 'Utility/flowToastMap'

import './GenericShopList.scss'

const GenericShopList = (props) => {
  const { isSmallScreen } = useMobileDetect()
  const [pointOfSales, setPointOfSales] = useState([])
  const [allDisputeShops, setAllDisputeShops] = useState([]) // Store all dispute shops
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 15,
    pageCount: 1
  })
  const [hasFetchedShops, setHasFetchedShops] = useState(false)
  const { setPopupClickAnalytics } = useContext(AnalyticsContext)

  // Check if we're in dispute context with specific shops to display
  const disputeShops = props.disputeShops || []
  const isDisputeContext = disputeShops.length > 0

  useEffect(() => {
    setPopupClickAnalytics && setPopupClickAnalytics(
      formattedMessage.title,
      listPointOfSaleFormattedMessage.sectionTitle,
      null,
      null,
      formattedMessage.closeLabel,
      false
    )
  }, [])

  useEffect(() => {
    if (props.open) {
      if (isDisputeContext && disputeShops.length > 0) {
        // In dispute context, store all shops and handle pagination locally
        const formattedDisputeShops = disputeShops.map(shop => ({
          name: capitalizeFirst(shop.shopName),
          code: shop.shopCode,
          address: capitalizeFirst(shop.address || ''),
          typology: shop.typology || '' // Now we have typology from reducer
        }))
        setAllDisputeShops(formattedDisputeShops)

        // Set up pagination
        const pageSize = 15
        const totalPages = Math.ceil(disputeShops.length / pageSize)
        setPagination({
          total: disputeShops.length,
          page: 1,
          pageSize,
          pageCount: totalPages
        })

        // Show first page
        setPointOfSales(formattedDisputeShops.slice(0, pageSize))
      } else {
        // Default behavior: fetch all shops but guard against repeated fetches
        // Only trigger if not already loading and we don't already have data
        const hasData = Array.isArray(props.pointOfSalesData) && props.pointOfSalesData.length > 0
        const isLoading = !!props.gtShopListLoading

        if (!isLoading && !hasData) {
          props.actions.doGetListOfShopsGt?.(0, 15)
          setPagination(prev => ({ ...prev, page: 1 }))
        }
      }
    }
    // Note: avoid using disputeShops (array) as a dependency to prevent
    // re-triggering the effect when its reference changes. Use primitive flag
    // isDisputeContext and props.open instead.
  }, [isDisputeContext])

  useEffect(() => {
  // Aggiorna il flag quando la fetch è stata fatta (loading finito e chiamata partita)
    if (!isDisputeContext && !props.gtShopListLoading && props.gtShopListPagination) {
      setHasFetchedShops(true)
    }

    // Mostra il toast solo se la fetch è avvenuta almeno una volta
    if (
      !isDisputeContext &&
    !props.gtShopListLoading &&
    Array.isArray(props.pointOfSalesData) &&
    props.pointOfSalesData.length === 0 &&
    hasFetchedShops
    ) {
      props.actions.doShowToast?.(flowToastMapType.ERROR_TOAST, {
        label: formattedMessage.noShopsAvailable
      })
    } else if (
      !isDisputeContext &&
    props.pointOfSalesData?.length > 0 &&
    !props.gtShopListLoading
    ) {
      setPointOfSales(props.pointOfSalesData)
    }
  }, [props.pointOfSalesData, props.gtShopListLoading, props.gtShopListPagination, hasFetchedShops])

  // Update pagination when pagination data changes (only if not in dispute context)
  useEffect(() => {
    if (!isDisputeContext && props.gtShopListPagination) {
      const { total, pageNumber, pageSize } = props.gtShopListPagination
      const pageCount = total && pageSize ? Math.ceil(total / pageSize) : 1
      setPagination({
        total,
        page: pageNumber + 1, // Convert 0-based to 1-based
        pageSize,
        pageCount
      })
    }
  }, [props.gtShopListPagination, isDisputeContext])

  const handleClose = () => {
    props.actions.doClosePopup?.('GENERIC_SHOP_LIST')
    setPopupClickAnalytics && setPopupClickAnalytics(
      formattedMessage.title,
      listPointOfSaleFormattedMessage.sectionTitle,
      null,
      null,
      formattedMessage.closeLabel,
      formattedMessage.closeLabel
    )
  }

  const handlePageChange = (page) => {
    if (isDisputeContext) {
      // Handle local pagination for dispute context
      const { pageSize } = pagination
      const startIndex = (page - 1) * pageSize
      const endIndex = startIndex + pageSize
      const pageData = allDisputeShops.slice(startIndex, endIndex)

      setPointOfSales(pageData)
      setPagination(prev => ({ ...prev, page }))
    } else {
      // Handle API pagination for normal context
      const pageNumber = page - 1 // Convert 1-based to 0-based for API
      props.actions.doGetListOfShopsGt?.(pageNumber, pagination.pageSize)
      setPagination(prev => ({ ...prev, page }))
    }
  }

  return (
    <Toggle>
      <Dialog fullScreen open={props.open} className='shopListDialog'>
        <FlowTemplate
          flowBackground
          titleNavigation={formattedMessage.title}
          iconCloseRight={<Dismiss size={isSmallScreen ? '24' : '20'} type='line' />}
          labelButtonRightNav={formattedMessage.closeLabel}
          onClickClose={handleClose}
          emptyPage
        >
          {!props.gtShopListLoading
            ? (
                pointOfSales.length > 0 && (
                  <div className='genericShopList'>
                    <ListPointOfSales
                      open
                      pointOfSalesData={pointOfSales}
                      onClickClose={handleClose}
                      pagination={pagination}
                      onPageChange={handlePageChange}
                      showPagination={pagination.total > pagination.pageSize}
                      sectionTitle={isDisputeContext ? formattedMessage.sectionTitle : null}
                    />
                  </div>
                )
              )
            : (
              <div className='shopPickerLoading'>
                <PortalLoader circular centered />
              </div>
              )}

        </FlowTemplate>
      </Dialog>
    </Toggle>
  )
}

GenericShopList.propTypes = {
  open: PropTypes.bool.isRequired,
  actions: PropTypes.object,
  pointOfSalesData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      typology: PropTypes.string,
      code: PropTypes.string,
      address: PropTypes.string
    })
  ),
  gtShopListLoading: PropTypes.bool,
  gtShopListPagination: PropTypes.shape({
    total: PropTypes.number,
    pageNumber: PropTypes.number,
    pageSize: PropTypes.number
  }),
  disputeShops: PropTypes.arrayOf(
    PropTypes.shape({
      shopCode: PropTypes.string,
      shopName: PropTypes.string,
      address: PropTypes.string,
      typology: PropTypes.string
    })
  )
}

GenericShopList.defaultProps = {
  actions: {},
  pointOfSalesData: [],
  gtShopListLoading: false,
  gtShopListPagination: {
    total: 0,
    pageNumber: 0,
    pageSize: 15
  },
  disputeShops: []
}

export default GenericShopList
